import React, { useState, useEffect, useRef, Suspense } from 'react';
import Login from './components/Login';
import PasswordReset from './components/PasswordReset';
import Dashboard from './components/Dashboard';
import ProjectList from './components/ProjectList';
import UpdateNotification from './components/UpdateNotification';
import ErrorBoundary from './components/ErrorBoundary';
import BottomTabBar, { TabId } from './components/BottomTabBar';
import DesktopSidebar from './components/DesktopSidebar';
import { useDesktopLayout } from './hooks/useDesktopLayout';
import {
  db, initializeDatabase, initializeMockUsers, getCurrentUser, deleteProject, logout,
  User, Project, Typology, MappingEntry, FloorPlan, StructureEntry,
  getFloorPlanBlobUrl, ensureFloorPlanAsset, updateFloorPlan, createFloorPlanPoint, updateFloorPlanPoint, getFloorPlanPoints, deleteFloorPlanPoint,
  getMappingEntriesForProject, getPhotosForMappings, getMappingEntry, getStructureEntry,
  updateFloorPlanLabelsForMapping, renumberFloorInterventions, getNextInterventionNumber,
  createMappingEntry, createStructureEntry, deleteMappingEntry, deleteStructureEntry,
} from './db';
import { apiFetchJson } from './lib/homeserver';
import { enforceForcedMigrationIfNeeded } from './lib/forcedMigration';
import type { UnmappedEntry, EntryRef, LinkedRecordUpdate } from './components/FloorPlanEditor';
import { determinePointTypeFromCrossings } from './utils/pointType';
import {
  startAutoSync, stopAutoSync, lockedSync, triggerImmediateDownload,
  getSyncStats, manualSync, clearAndSync, PendingChangesError, SyncStats, SyncProgress, onSyncComplete, offSyncComplete
} from './sync/syncEngine';
import { eventStream, tabId } from './realtime/eventStream';
import { handleProjectDeleteLocal, drainPendingCascades } from './realtime/projectCascade';
import type { SubTab } from './components/ProjectDetail';
import './App.css';

// Lazy-loaded components: these pull in heavy libraries (jsPDF, pdf-lib, pdfjs-dist, xlsx)
// and are only needed when the user navigates to specific views
const ProjectForm = React.lazy(() => import('./components/ProjectForm'));
const ProjectDetail = React.lazy(() => import('./components/ProjectDetail'));
const MappingWizard = React.lazy(() => import('./components/MappingWizard'));
const StructureWizard = React.lazy(() => import('./components/StructureWizard'));
const MapsOverview = React.lazy(() => import('./components/MapsOverview'));
const SettingsPage = React.lazy(() => import('./components/SettingsPage'));
const ReportsPage = React.lazy(() => import('./components/ReportsPage'));
const StandaloneFloorPlanEditor = React.lazy(() => import('./components/StandaloneFloorPlanEditor'));
const FloorPlanEditor = React.lazy(() => import('./components/FloorPlanEditor'));

type View = 'login' | 'passwordReset' | 'tabs' | 'projectForm' | 'projectEdit' | 'mapping' | 'structure' | 'projectDetail' | 'standaloneEditor' | 'floorPlanEditor' | 'reports';

// Etichetta mostrata sul punto della planimetria per una mappatura.
// Usata sia al caricamento dell'editor sia dopo la rinumerazione degli interventi.
const buildMappingLabelText = (project: Project, m: MappingEntry, photoCount: number): string[] => {
  const parts: string[] = [];
  if (project.floors && project.floors.length > 1) parts.push(`P${m.floor}`);
  if (project.useRoomNumbering && m.room) parts.push(`S${m.room}`);
  if (project.useInterventionNumbering && m.intervention) parts.push(`Int${m.intervention}`);
  let firstLine = parts.join('_') || 'Punto';
  if (photoCount > 1) firstLine += `_01-${photoCount.toString().padStart(2, '0')}`;
  const tipNums = m.crossings
    .map(c => c.tipologicoId ? project.typologies?.find(t => t.id === c.tipologicoId)?.number : null)
    .filter((n): n is number => n !== null)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort((a, b) => a - b);
  return [firstLine, tipNums.length > 0 ? `Tip. ${tipNums.join(' - ')}` : ''].filter(Boolean);
};

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState<View>('login');
  const [resetToken, setResetToken] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  // Schermata da cui e' stata aperta la scheda Segnalazioni: finisce nel
  // contesto tecnico allegato, che altrimenti direbbe sempre 'reports'.
  const vistaPrimaDelleSegnalazioni = useRef<string>('tabs');
  // Sotto-scheda su cui riaprire ProjectDetail. Gli editor (mappature,
  // strutture, planimetrie) sostituiscono la view e ne provocano lo smontaggio:
  // senza questo, al rientro si ripartirebbe sempre da 'mappings'.
  const sottoSchedaDettaglio = useRef<SubTab | undefined>(undefined);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentMappingProject, setCurrentMappingProject] = useState<Project | null>(null);
  const [currentStructureProject, setCurrentStructureProject] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [editingMappingEntry, setEditingMappingEntry] = useState<MappingEntry | undefined>(undefined);
  const [editingStructureEntry, setEditingStructureEntry] = useState<StructureEntry | undefined>(undefined);
  const [mappingWizardKey, setMappingWizardKey] = useState(0);
  const [structureWizardKey, setStructureWizardKey] = useState(0);
  const [editingMappingEntryInitialStep, setEditingMappingEntryInitialStep] = useState(0);
  const [editingStructureEntryInitialStep, setEditingStructureEntryInitialStep] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [syncStats, setSyncStats] = useState<SyncStats>({
    pendingCount: 0,
    lastSyncTime: null,
    isSyncing: false
  });
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);

  const isDesktop = useDesktopLayout();

  // Floor plan editor state for maps tab
  const [editorFloorPlan, setEditorFloorPlan] = useState<FloorPlan | null>(null);
  const [editorImageUrl, setEditorImageUrl] = useState<string | null>(null);
  const [editorProject, setEditorProject] = useState<Project | null>(null);
  const [editorInitialPoints, setEditorInitialPoints] = useState<import('./components/FloorPlanCanvas').CanvasPoint[]>([]);
  const [editorUnmappedEntries, setEditorUnmappedEntries] = useState<UnmappedEntry[]>([]);
  // Scheda (mappatura o struttura) aperta in un modal sopra l'editor planimetria.
  // `initialStep` parte da 0 per una scheda nuova, così non si salta la posizione,
  // e da 1 quando si apre una scheda esistente dall'elenco punti.
  const [editorRecordModal, setEditorRecordModal] = useState<
    | { kind: 'mapping'; entry: MappingEntry; initialStep: 0 | 1 }
    | { kind: 'structure'; entry: StructureEntry; initialStep: 0 | 1 }
    | null
  >(null);

  // Ultimo aggiornamento da riportare sul punto collegato alla scheda appena chiusa
  const [editorRecordUpdate, setEditorRecordUpdate] = useState<LinkedRecordUpdate | null>(null);

  // Refs per stale-view guard (evitano stale closure nell'handler SSE)
  const viewingProjectRef = useRef<Project | null>(null);
  const currentMappingProjectRef = useRef<Project | null>(null);
  const currentStructureProjectRef = useRef<Project | null>(null);
  const editorProjectRef = useRef<Project | null>(null);
  const selectedProjectRef = useRef<Project | null>(null);

  // Mantieni i ref allineati allo stato
  useEffect(() => { viewingProjectRef.current = viewingProject; }, [viewingProject]);
  useEffect(() => { currentMappingProjectRef.current = currentMappingProject; }, [currentMappingProject]);
  useEffect(() => { currentStructureProjectRef.current = currentStructureProject; }, [currentStructureProject]);
  useEffect(() => { editorProjectRef.current = editorProject; }, [editorProject]);
  useEffect(() => { selectedProjectRef.current = selectedProject; }, [selectedProject]);

  // Col modal della scheda aperto, il tasto indietro chiude la scheda e lascia
  // l'editor dov'era. La voce di cronologia viene aggiunta all'apertura (prima che
  // il wizard aggiunga la sua), così alla chiusura la pila torna com'era.
  useEffect(() => {
    if (!editorRecordModal) return;

    const handleModalPopState = () => { void handleCloseEditorRecordModal(); };
    window.addEventListener('popstate', handleModalPopState);
    return () => window.removeEventListener('popstate', handleModalPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRecordModal]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as { view?: View; tab?: TabId } | null;
      if (state?.view) {
        setCurrentView(state.view);
        if (state.tab) setActiveTab(state.tab);
        if (state.view === 'tabs') {
          setSelectedProject(null);
          setCurrentMappingProject(null);
          setCurrentStructureProject(null);
          setViewingProject(null);
          setEditingMappingEntry(undefined);
          setEditingStructureEntry(undefined);
        }
      } else {
        window.history.pushState({ view: currentView, tab: activeTab }, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    if (!window.history.state) {
      window.history.replaceState({ view: currentView, tab: activeTab }, '', window.location.href);
    }
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentView, activeTab]);

  // Push history on view change
  useEffect(() => {
    if (window.history.state?.view === currentView && window.history.state?.tab === activeTab) return;
    window.history.pushState({ view: currentView, tab: activeTab }, '', window.location.href);
  }, [currentView, activeTab]);

  // Initialize database
  useEffect(() => {
    const initialize = async () => {
      try {
        if (await enforceForcedMigrationIfNeeded()) {
          return;
        }

        await initializeDatabase();
        await initializeMockUsers();

        const locationHash = window.location?.hash ?? '';
        const pathname = window.location?.pathname ?? '/';
        const search = window.location?.search ?? '';
        const hashParams = new URLSearchParams(locationHash.substring(1));
        const queryParams = new URLSearchParams(search);
        const type = hashParams.get('type');
        const accessToken = hashParams.get('access_token');
        // better-auth: dopo la validazione del link, redirige all'app con ?token=...
        const resetTokenFromUrl = queryParams.get('token');

        if (resetTokenFromUrl || type === 'recovery' || pathname === '/reset-password') {
          if (resetTokenFromUrl) setResetToken(resetTokenFromUrl);
          setCurrentView('passwordReset');
          setIsInitialized(true);
          return;
        }

        if (type === 'signup' && accessToken) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          window.history.replaceState(null, '', pathname);
        }

        const user = await getCurrentUser();
        if (user) {
          setCurrentUser(user);
          setCurrentView('tabs');
          if (type === 'signup' && accessToken) {
            alert('Email confirmed! Welcome to OPImaPPA.');
          }
        }

        if (navigator.onLine) {
          startAutoSync(60000);
        }

        updateSyncStats();
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        alert('Failed to initialize app. Please refresh the page.');
      }
    };

    initialize();
    return () => { stopAutoSync(); };
  }, []);

  const updateSyncStats = async () => {
    const stats = await getSyncStats();
    setSyncStats(stats);
  };

  // Online/offline monitoring
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      if (navigator.onLine) {
        try {
          await lockedSync();
          await updateSyncStats();
        } catch (err) {
          console.error('Sync after reconnection failed:', err);
        }
      }
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync stats event listener
  useEffect(() => {
    const handler = (stats: SyncStats) => setSyncStats(stats);
    onSyncComplete(handler);
    updateSyncStats();
    return () => offSyncComplete(handler);
  }, []);

  // EventStream: connessione SSE realtime (solo se autenticato e inizializzato)
  useEffect(() => {
    if (!isInitialized || !currentUser) return;
    let active = true;

    // Guard: se il progetto correntemente visualizzato viene eliminato da remoto,
    // torna alla vista tabs e azzera tutti i riferimenti al progetto.
    const handleStaleView = async (ev: import('./realtime/eventStream').ChangeLogRow) => {
      if (ev.table_name !== 'projects' || ev.op !== 'DELETE') return;
      const deletedId = ev.row_id;
      const isStale =
        viewingProjectRef.current?.id === deletedId ||
        currentMappingProjectRef.current?.id === deletedId ||
        currentStructureProjectRef.current?.id === deletedId ||
        editorProjectRef.current?.id === deletedId ||
        selectedProjectRef.current?.id === deletedId;
      if (!isStale) return;
      setCurrentView('tabs');
      setViewingProject(null);
      setCurrentMappingProject(null);
      setCurrentStructureProject(null);
      setEditorProject(null);
      setSelectedProject(null);
      // Fix H3: azzera lo stato del floor plan editor
      setEditorFloorPlan(null);
      setEditorImageUrl(null);
      // Fix M3: azzera le entry in modifica (come fa handlePopState quando torna a 'tabs')
      setEditingMappingEntry(undefined);
      setEditingStructureEntry(undefined);
      // Fix M4: sostituisce la history entry stale per evitare che il tasto Back la ripristini
      window.history.replaceState({ view: 'tabs', tab: activeTab }, '', window.location.pathname);
    };

    // F2: qualsiasi modifica remota (insert/update/delete) su dati propaga subito
    // un download incrementale, invece di attendere il poll periodico (60s).
    // Salta gli eventi originati da QUESTO tab: sono già stati applicati localmente
    // e caricati via triggerImmediateUpload, quindi un download li ri-scaricherebbe
    // solo per nulla vanificando F1.
    const handleRemoteDataChange = async (ev: import('./realtime/eventStream').ChangeLogRow): Promise<void> => {
      if (ev.originator && ev.originator === tabId) return;
      triggerImmediateDownload();
    };

    const run = async () => {
      await eventStream.init();
      if (!active) return;
      // B8: riprova i cascade delete rimasti in sospeso (es. quota IndexedDB) prima di riprendere lo stream.
      await drainPendingCascades();
      if (!active) return;
      eventStream.subscribe(handleProjectDeleteLocal);
      eventStream.subscribe(handleStaleView);
      eventStream.subscribe(handleRemoteDataChange);
      eventStream.start();
    };
    run();
    return () => {
      active = false;
      eventStream.unsubscribe(handleProjectDeleteLocal);
      eventStream.unsubscribe(handleStaleView);
      eventStream.unsubscribe(handleRemoteDataChange);
      eventStream.stop();
    };
    // activeTab è letto solo in replaceState dentro handleStaleView: includerlo
    // nei deps riconnetterebbe l'SSE a ogni cambio tab. Voluto fuori.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, currentUser]);

  // Service Worker message handler
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'BACKGROUND_SYNC') {
        try {
          await lockedSync();
          await updateSyncStats();
        } catch {}
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMessage);
  }, []);

  // Background sync registration
  useEffect(() => {
    if (isOnline && syncStats.pendingCount > 0) {
      (async () => {
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
          try {
            const reg = await navigator.serviceWorker.ready;
            await (reg as any).sync.register('sync-queue');
          } catch {}
        }
      })();
    }
  }, [isOnline, syncStats.pendingCount]);

  // SW update listener
  useEffect(() => {
    const handler = (event: Event) => {
      setSwRegistration((event as CustomEvent).detail as ServiceWorkerRegistration);
    };
    window.addEventListener('swUpdate', handler);
    return () => window.removeEventListener('swUpdate', handler);
  }, []);

  // Handlers
  const handleLogin = (user: User) => {
    setCurrentUser(user);
    setCurrentView('tabs');
    setActiveTab('dashboard');
  };

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentUser(null);
      setCurrentView('login');
    } catch {
      alert('Logout failed. Please try again.');
    }
  };

  const handleManualSync = async () => {
    if (!navigator.onLine) { alert('Impossibile sincronizzare: nessuna connessione.'); return; }
    try {
      setSyncStats(prev => ({ ...prev, isSyncing: true }));
      setSyncProgress({ step: 0, totalSteps: 6, phase: 'Avvio sincronizzazione...' });
      const result = await manualSync({
        onPhotoDecisionNeeded: () => Promise.resolve(
          window.confirm('Sincronizzare anche le foto?')
        ),
        onProgress: setSyncProgress,
      });
      await updateSyncStats();
      const d = result.downloadResult;
      const u = result.uploadResult;
      setSyncProgress({
        step: 6, totalSteps: 6,
        phase: 'Sync completato',
        detail: `${u.processedCount} caricati, ${d.projectsCount} prog., ${d.entriesCount} map., ${d.floorPlansCount} plan.${d.photosCount > 0 ? `, ${d.photosCount} foto` : ''}`
      });
      // Auto-dismiss after 4 seconds
      setTimeout(() => setSyncProgress(null), 4000);
    } catch {
      setSyncProgress({ step: 0, totalSteps: 6, phase: 'Errore sincronizzazione', detail: 'Riprova più tardi' });
      setSyncStats(prev => ({ ...prev, isSyncing: false }));
      setTimeout(() => setSyncProgress(null), 4000);
    }
  };

  const handleOpenReports = () => {
    vistaPrimaDelleSegnalazioni.current = `${currentView}/${activeTab}`;
    setCurrentView('reports');
  };

  const handleClearAndSync = async () => {
    if (!navigator.onLine) { alert('Impossibile sincronizzare: nessuna connessione.'); return; }

    // Il reset cancella i dati locali: avvisiamo esplicitamente se ci sono
    // modifiche non ancora arrivate sul server (foto e voci raccolte in cantiere).
    const pending = (await getSyncStats()).pendingCount;
    const avviso = pending > 0
      ? `ATTENZIONE: ci sono ${pending} modifiche non ancora sincronizzate (foto o voci raccolte).\n\n` +
        'Verranno caricate sul server PRIMA del reset. Se il caricamento non riesce, il reset viene annullato per non perderle.\n\n' +
        'Procedere?'
      : 'Reimpostare la cache locale e reidratare i metadati dal server?';
    if (!window.confirm(avviso)) return;

    try {
      setSyncStats(prev => ({ ...prev, isSyncing: true }));
      await clearAndSync();
      window.location.reload();
    } catch (err) {
      if (err instanceof PendingChangesError) {
        alert(
          `Reset annullato: ${err.pendingCount} modifiche locali non sono ancora sul server e sarebbero andate perse.\n\n` +
          'I tuoi dati sono ancora sul dispositivo. Verifica la connessione, premi Sincronizza e riprova il reset solo quando le modifiche in sospeso sono a zero.'
        );
      } else {
        alert('Reset cache non riuscito. Nessun dato è stato cancellato.');
      }
      setSyncStats(prev => ({ ...prev, isSyncing: false }));
    }
  };

  const handleCreateProject = () => {
    setSelectedProject(null);
    setCurrentView('projectForm');
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setCurrentView('projectEdit');
  };

  const handleViewProject = (project: Project) => {
    sottoSchedaDettaglio.current = undefined; // apertura pulita: default 'mappings'
    setViewingProject(project);
    setCurrentView('projectDetail');
  };

  const handleEnterMapping = (project: Project) => {
    setCurrentMappingProject(project);
    setEditingMappingEntry(undefined);
    setCurrentView('mapping');
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
    } catch {
      alert('Failed to delete project.');
    }
  };

  const handleProjectSaved = () => {
    setCurrentView('tabs');
    setActiveTab('projects');
    setSelectedProject(null);
  };

  const handleBackFromMapping = () => {
    sottoSchedaDettaglio.current = 'mappings';
    if (viewingProject) {
      setCurrentView('projectDetail');
    } else {
      setCurrentView('tabs');
    }
    setCurrentMappingProject(null);
    setEditingMappingEntry(undefined);
    setEditingMappingEntryInitialStep(0);
  };

  // Gli snapshot del progetto tenuti qui non si rinfrescano da soli: se non li
  // allineiamo, un wizard che si rimonta (nuova scheda) o il dettaglio progetto
  // ripartono dai tipologici vecchi e il salvataggio successivo li sovrascrive.
  const handleTypologiesChanged = (projectId: string, typologies: Typology[]) => {
    const patch = (p: Project | null) => (p && p.id === projectId ? { ...p, typologies } : p);
    setCurrentMappingProject(patch);
    setCurrentStructureProject(patch);
    setViewingProject(patch);
    setEditorProject(patch);
  };

  const handleMappingSaved = () => {
    setEditingMappingEntry(undefined);
    setMappingWizardKey(k => k + 1);
    setEditingMappingEntryInitialStep(0);
  };

  const handleEnterStructure = (project: Project) => {
    setCurrentStructureProject(project);
    setEditingStructureEntry(undefined);
    setCurrentView('structure');
  };

  const handleBackFromStructure = () => {
    sottoSchedaDettaglio.current = 'structures';
    if (viewingProject) {
      setCurrentView('projectDetail');
    } else {
      setCurrentView('tabs');
    }
    setCurrentStructureProject(null);
    setEditingStructureEntry(undefined);
    setEditingStructureEntryInitialStep(0);
  };

  const handleStructureSaved = () => {
    setEditingStructureEntry(undefined);
    setStructureWizardKey(k => k + 1);
    setEditingStructureEntryInitialStep(0);
  };

  const handleAddMappingFromDetail = () => {
    if (viewingProject) {
      handleEnterMapping(viewingProject);
    }
  };

  const handleEditMappingFromDetail = (entry: MappingEntry) => {
    if (viewingProject) {
      setCurrentMappingProject(viewingProject);
      setEditingMappingEntry(entry);
      setCurrentView('mapping');
    }
  };

  const handleAddStructureFromDetail = () => {
    if (viewingProject) {
      handleEnterStructure(viewingProject);
    }
  };

  const handleEditStructureFromDetail = (entry: StructureEntry) => {
    if (viewingProject) {
      setCurrentStructureProject(viewingProject);
      setEditingStructureEntry(entry);
      setCurrentView('structure');
    }
  };

  const handleOpenFloorPlanEditor = async (project: Project, floorPlan: FloorPlan) => {
    // Check remote version before opening to warn about concurrent edits
    if (isOnline) {
      try {
        const { data } = await apiFetchJson<{ data: { updated_at: string }[] }>(
          `/api/floor_plans?id=eq.${floorPlan.id}&select=updated_at&limit=1`
        );
        const remoteUpdatedAtValue = data?.[0]?.updated_at;

        if (remoteUpdatedAtValue) {
          const remoteUpdatedAt = new Date(remoteUpdatedAtValue).getTime();
          // Leggi da Dexie per avere remoteUpdatedAt aggiornato dopo upload (evita falsi conflitti)
          const freshLocal = await db.floorPlans.get(floorPlan.id);
          const localBase = (freshLocal ?? floorPlan).remoteUpdatedAt ?? (freshLocal ?? floorPlan).updatedAt;
          // Warn if remote is more than 5s newer than our base version (tolerance for clock skew)
          if (remoteUpdatedAt > localBase + 5000) {
            const localDate = new Date(localBase).toLocaleString('it-IT');
            const remoteDate = new Date(remoteUpdatedAt).toLocaleString('it-IT');
            const proceed = window.confirm(
              `⚠️ Attenzione: questa planimetria è stata modificata da un altro utente.\n\n` +
              `Versione remota: ${remoteDate}\n` +
              `Versione locale:  ${localDate}\n\n` +
              `Si consiglia di sincronizzare prima di modificare per non perdere le modifiche altrui.\n\n` +
              `Continuare comunque?`
            );
            if (!proceed) return;
          }
        }
      } catch {
        // Network error or plan not yet on remote → open normally
      }
    }

    setEditorProject(project);
    const hydratedPlan = await ensureFloorPlanAsset(floorPlan.id, 'full') || floorPlan;
    setEditorFloorPlan(hydratedPlan);
    if (editorImageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(editorImageUrl);
    }
    const hydratedImageUrl = getFloorPlanBlobUrl(hydratedPlan.imageBlob, hydratedPlan.imageUrl);
    if (!hydratedImageUrl) {
      alert('Immagine planimetria non disponibile. Verifica la connessione e riprova.');
      return;
    }
    setEditorImageUrl(hydratedImageUrl);
    await loadFloorPlanEditorPoints(project, hydratedPlan);
    setCurrentView('floorPlanEditor');
  };

  // Carica punti ed etichette della planimetria nell'editor. Serve all'apertura e
  // di nuovo quando si modifica una scheda nel modal, per riallineare le etichette.
  const loadFloorPlanEditorPoints = async (project: Project, hydratedPlan: FloorPlan) => {
    try {
      const dbPoints = await getFloorPlanPoints(hydratedPlan.id);
      const mappings = await getMappingEntriesForProject(project.id);
      const photosByMapping = await getPhotosForMappings(mappings.map((m) => m.id));
      const placedMappingIds = new Set(dbPoints.map((p) => p.mappingEntryId).filter(Boolean));

      const buildMappingLabel = (m: MappingEntry, photoCount: number) => buildMappingLabelText(project, m, photoCount);

      const canvasPoints = dbPoints.map(p => {
        const mappingEntry = mappings.find(m => m.id === p.mappingEntryId);
        const labelText = mappingEntry
          ? buildMappingLabel(mappingEntry, (photosByMapping[mappingEntry.id] || []).length)
          : (p.metadata?.labelText || ['Punto']);
        return {
          id: p.id,
          type: p.pointType as import('./components/FloorPlanCanvas').CanvasPoint['type'],
          pointX: p.pointX,
          pointY: p.pointY,
          labelX: p.labelX,
          labelY: p.labelY,
          labelText,
          perimeterPoints: p.perimeterPoints,
          mappingEntryId: p.mappingEntryId,
          structureEntryId: p.structureEntryId,
          labelBackgroundColor: p.metadata?.labelBackgroundColor,
          labelTextColor: p.metadata?.labelTextColor,
          eiRating: p.eiRating,
        };
      });
      setEditorInitialPoints(canvasPoints);
      const unmappedEntries: UnmappedEntry[] = mappings
        .filter((m) => m.floor === hydratedPlan.floor && !placedMappingIds.has(m.id))
        .map((m) => {
          const photoCount = (photosByMapping[m.id] || []).length;
          const supporto = m.crossings?.[0]?.supporto?.toLowerCase() || '';
          return {
            id: m.id,
            labelText: buildMappingLabel(m, photoCount),
            type: (supporto === 'solaio' ? 'solaio' : 'parete') as 'parete' | 'solaio',
          };
        });
      setEditorUnmappedEntries(unmappedEntries);
    } catch (err) {
      console.warn('Could not load floor plan points:', err);
      setEditorInitialPoints([]);
      setEditorUnmappedEntries([]);
    }
  };

  // Rinumera gli interventi seguendo l'ordine sinistra→destra dei punti sulla
  // planimetria (l'editor passa le voci già ordinate per X). Mappature e strutture
  // condividono un'unica sequenza sul piano: le voci del piano non posizionate sulla
  // planimetria prendono i numeri successivi, altrimenti resterebbero dei doppioni.
  // Scrive i numeri intervento veri, non solo le etichette.
  const handleReorderEditorPoints = async (sortedEntries: EntryRef[]) => {
    if (!editorProject || !editorFloorPlan) return editorInitialPoints;
    const project = editorProject;
    const floor = editorFloorPlan.floor;

    const orderedEntryIds = sortedEntries
      .map(ref => ref.mappingEntryId || ref.structureEntryId)
      .filter((id): id is string => Boolean(id));

    const { mappingIds } = await renumberFloorInterventions({
      projectId: project.id,
      floor,
      orderedEntryIds,
      userId: currentUser?.id,
    });

    // Etichette delle mappature: qui sono più ricche di quelle scritte dal modulo
    // (tipologici e numero foto), quindi le ricalcola App
    const renumbered = await Promise.all(mappingIds.map((id: string) => getMappingEntry(id)));
    const photosByMapping = await getPhotosForMappings(mappingIds);
    const labelByMapping = new Map<string, string[]>();

    for (const mapping of renumbered) {
      if (!mapping) continue;
      const photoCount = (photosByMapping[mapping.id] || []).length;
      const label = buildMappingLabelText(project, mapping, photoCount);
      labelByMapping.set(mapping.id, label);
      try {
        await updateFloorPlanLabelsForMapping(mapping.id, () => label);
      } catch (err) {
        console.error(`Impossibile aggiornare l'etichetta della planimetria per ${mapping.id}:`, err);
      }
    }

    const updatedPoints = editorInitialPoints.map(point => {
      const label = point.mappingEntryId ? labelByMapping.get(point.mappingEntryId) : undefined;
      return label ? { ...point, labelText: label } : point;
    });
    setEditorInitialPoints(updatedPoints);

    return updatedPoints;
  };

  // Apre la scheda in modal registrando la voce di cronologia, così il tasto indietro
  // chiude la scheda invece di uscire dall'editor. La voce conserva `view`.
  const openEditorRecordModal = (modal: NonNullable<typeof editorRecordModal>) => {
    window.history.pushState({ ...(window.history.state || {}), __recordModal: true }, '');
    setEditorRecordModal(modal);
  };

  // Flusso inverso: si piazza il punto sulla planimetria e la scheda si apre subito.
  // La voce viene creata già qui, come bozza "da completare" con il primo numero
  // intervento libero sul piano, per due motivi: il punto ha da subito qualcosa a cui
  // agganciarsi, e il numero risulta occupato anche se la scheda viene annullata,
  // altrimenti lo riprenderebbe la voce successiva.
  const handleNewPointRecord = async (point: import('./components/FloorPlanCanvas').CanvasPoint) => {
    if (!editorProject || !editorFloorPlan || !currentUser) return null;
    const project = editorProject;
    const floor = editorFloorPlan.floor;

    const intervention = project.useInterventionNumbering
      ? await getNextInterventionNumber(project.id, floor)
      : undefined;

    try {
      if (point.type === 'struttura') {
        const draft = await createStructureEntry({
          projectId: project.id,
          floor,
          intervention,
          toComplete: true,
          structures: [],
          createdBy: currentUser.id,
        }, []);
        openEditorRecordModal({ kind: 'structure', entry: draft, initialStep: 0 });
        return {
          structureEntryId: draft.id,
          labelText: [intervention ? `P${floor}_Int${intervention}` : `P${floor}`],
        };
      }

      const draft = await createMappingEntry({
        projectId: project.id,
        floor,
        intervention,
        toComplete: true,
        crossings: [],
        createdBy: currentUser.id,
      }, []);
      openEditorRecordModal({ kind: 'mapping', entry: draft, initialStep: 0 });
      return {
        mappingEntryId: draft.id,
        labelText: buildMappingLabelText(project, draft, 0),
      };
    } catch (err) {
      console.error('Impossibile creare la scheda per il nuovo punto:', err);
      alert('Errore nella creazione della scheda collegata al punto.');
      return null;
    }
  };

  // Chiude la scheda aperta in modal sopra l'editor e ricalcola punti ed etichette:
  // l'editor resta montato e riallinea solo il testo delle etichette, quindi zoom,
  // selezione e modifiche non salvate restano al loro posto.
  const handleCloseEditorRecordModal = async () => {
    if (!editorRecordModal) return;
    const closed = editorRecordModal;
    setEditorRecordModal(null);

    // Consuma la voce di cronologia aggiunta all'apertura, così il tasto indietro
    // successivo esce dall'editor invece di non fare nulla.
    if ((window.history.state as { __recordModal?: boolean } | null)?.__recordModal) {
      window.history.back();
    }

    if (editorProject && editorFloorPlan) {
      await loadFloorPlanEditorPoints(editorProject, editorFloorPlan);
    }

    // Riporta sul punto quanto deciso nella scheda: il marcatore segue il supporto
    // degli attraversamenti, l'etichetta il numero intervento
    if (closed.kind === 'mapping' && editorProject) {
      const mapping = await getMappingEntry(closed.entry.id);
      if (mapping) {
        const photos = await getPhotosForMappings([mapping.id]);
        setEditorRecordUpdate({
          entryId: mapping.id,
          type: determinePointTypeFromCrossings(mapping.crossings),
          labelText: buildMappingLabelText(editorProject, mapping, (photos[mapping.id] || []).length),
        });
      }
    } else if (closed.kind === 'structure') {
      const structure = await getStructureEntry(closed.entry.id);
      if (structure) {
        const parts = [`P${structure.floor}`];
        if (structure.room) parts.push(`S${structure.room}`);
        if (structure.intervention) parts.push(`Int${structure.intervention}`);
        setEditorRecordUpdate({ entryId: structure.id, labelText: [parts.join('_')] });
      }
    }
  };

  // Elimina la scheda collegata a un punto, su richiesta dell'editor. L'eliminazione
  // porta con sé anche il punto già salvato sulla planimetria, quindi il riferimento
  // usato dal salvataggio va riletto.
  const handleDeleteLinkedRecord = async (entryId: string, kind: 'mapping' | 'structure') => {
    if (kind === 'structure') {
      await deleteStructureEntry(entryId);
    } else {
      await deleteMappingEntry(entryId);
    }

    if (editorProject && editorFloorPlan) {
      await loadFloorPlanEditorPoints(editorProject, editorFloorPlan);
    }
  };

  const handleBackFromFloorPlanEditor = () => {
    sottoSchedaDettaglio.current = 'plans';
    if (viewingProject) {
      setCurrentView('projectDetail');
    } else {
      setCurrentView('tabs');
      setActiveTab('maps');
    }
    // Revoke blob URL to prevent memory leak
    if (editorImageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(editorImageUrl);
    }
    setEditorFloorPlan(null);
    setEditorImageUrl(null);
    setEditorProject(null);
    setEditorInitialPoints([]);
    setEditorUnmappedEntries([]);
    setEditorRecordModal(null);
    setEditorRecordUpdate(null);
  };

  const handleOpenStandaloneEditor = () => {
    setCurrentView('standaloneEditor');
  };

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    // Reset sub-views when changing tabs
    if (currentView !== 'tabs') {
      setCurrentView('tabs');
    }
  };

  // Loading state
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-brand-500 text-sm">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Non-authenticated views
  if (currentView === 'passwordReset') {
    return <PasswordReset token={resetToken} onSuccess={() => {
      setResetToken('');
      setCurrentView('login');
      window.history.replaceState(null, '', window.location.pathname);
    }} />;
  }

  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  // Main app content
  const renderContent = () => {
    switch (currentView) {
      case 'projectForm':
      case 'projectEdit':
        return (
          <ProjectForm
            project={selectedProject}
            currentUser={currentUser}
            onSave={handleProjectSaved}
            onCancel={() => { setCurrentView('tabs'); setSelectedProject(null); }}
            onSync={handleManualSync}
            isSyncing={syncStats.isSyncing}
          />
        );

      case 'mapping':
        return (
          <MappingWizard
            key={mappingWizardKey}
            project={currentMappingProject}
            currentUser={currentUser}
            onBack={handleBackFromMapping}
            onSaved={handleMappingSaved}
            editingEntry={editingMappingEntry}
            onSync={handleManualSync}
            isSyncing={syncStats.isSyncing}
            initialStep={editingMappingEntryInitialStep as 0 | 1 | 2}
            onTypologiesChanged={handleTypologiesChanged}
          />
        );

      case 'structure':
        return (
          <StructureWizard
            key={structureWizardKey}
            project={currentStructureProject}
            currentUser={currentUser}
            onBack={handleBackFromStructure}
            onSaved={handleStructureSaved}
            editingEntry={editingStructureEntry}
            onSync={handleManualSync}
            isSyncing={syncStats.isSyncing}
            initialStep={editingStructureEntryInitialStep as 0 | 1 | 2}
            onTypologiesChanged={handleTypologiesChanged}
          />
        );

      case 'projectDetail':
        return viewingProject ? (
          <ProjectDetail
            project={viewingProject}
            currentUser={currentUser}
            onBack={() => { setViewingProject(null); setCurrentView('tabs'); setActiveTab('projects'); }}
            onAddMapping={handleAddMappingFromDetail}
            onEditMapping={handleEditMappingFromDetail}
            onAddStructure={handleAddStructureFromDetail}
            onEditStructure={handleEditStructureFromDetail}
            onOpenFloorPlanEditor={handleOpenFloorPlanEditor}
            initialSubTab={sottoSchedaDettaglio.current}
            onTypologiesChanged={handleTypologiesChanged}
            onSync={handleManualSync}
            isSyncing={syncStats.isSyncing}
          />
        ) : null;

      case 'standaloneEditor':
        return (
          <StandaloneFloorPlanEditor
            currentUser={currentUser}
            onBack={() => { setCurrentView('tabs'); setActiveTab('maps'); }}
          />
        );

      case 'floorPlanEditor':
        if (editorFloorPlan && editorImageUrl && editorProject) {
          return (
            <>
            <FloorPlanEditor
              imageUrl={editorImageUrl}
              initialPoints={editorInitialPoints}
              mode="view-edit"
              unmappedEntries={editorUnmappedEntries}
              initialGridConfig={editorFloorPlan.gridEnabled ? {
                enabled: editorFloorPlan.gridEnabled,
                rows: editorFloorPlan.gridConfig?.rows || 10,
                cols: editorFloorPlan.gridConfig?.cols || 10,
                offsetX: editorFloorPlan.gridConfig?.offsetX || 0,
                offsetY: editorFloorPlan.gridConfig?.offsetY || 0,
              } : undefined}
              initialCartiglio={editorFloorPlan.metadata?.cartiglio}
              defaultTavola={editorFloorPlan.floor}
              defaultCommittente={[editorProject.client.trim() || editorProject.title.trim(), editorProject.address.trim()].filter(Boolean).join(' - ')}
              typologyNumbers={[...(editorProject.typologies || [])].map(t => t.number).sort((a, b) => a - b)}
              onSave={async (points, gridConfig, cartiglio) => {
                try {
                  const initialIds = new Set(editorInitialPoints.map(p => p.id));
                  const currentPointIdSet = new Set(points.map(p => p.id));

                  // Elimina i punti rimossi
                  const deletedIds = editorInitialPoints.map(p => p.id).filter(id => !currentPointIdSet.has(id));
                  for (const id of deletedIds) {
                    await deleteFloorPlanPoint(id);
                  }

                  // Crea o aggiorna i punti correnti
                  for (const point of points) {
                    if (!initialIds.has(point.id)) {
                      await createFloorPlanPoint(
                        editorFloorPlan.id, point.mappingEntryId || '',
                        point.type, point.pointX, point.pointY,
                        point.labelX, point.labelY, currentUser.id,
                        {
                          perimeterPoints: point.perimeterPoints,
                          customText: point.customText,
                          eiRating: point.eiRating,
                          metadata: {
                            labelText: point.labelText,
                            labelBackgroundColor: point.labelBackgroundColor,
                            labelTextColor: point.labelTextColor,
                          },
                        }
                      );
                    } else {
                      await updateFloorPlanPoint(point.id, {
                        // Il tipo può cambiare: il marcatore segue il supporto scelto nella scheda
                        pointType: point.type,
                        pointX: point.pointX,
                        pointY: point.pointY,
                        labelX: point.labelX,
                        labelY: point.labelY,
                        perimeterPoints: point.perimeterPoints,
                        customText: point.customText,
                        eiRating: point.eiRating,
                        metadata: {
                          labelText: point.labelText,
                          labelBackgroundColor: point.labelBackgroundColor,
                          labelTextColor: point.labelTextColor,
                        },
                      });
                    }
                  }
                  await updateFloorPlan(editorFloorPlan.id, {
                    gridEnabled: gridConfig.enabled,
                    gridConfig: { rows: gridConfig.rows, cols: gridConfig.cols, offsetX: gridConfig.offsetX, offsetY: gridConfig.offsetY },
                    metadata: {
                      ...(editorFloorPlan.metadata || {}),
                      cartiglio,
                    },
                  });
                  setEditorFloorPlan(prev => prev ? {
                    ...prev,
                    gridEnabled: gridConfig.enabled,
                    gridConfig: { rows: gridConfig.rows, cols: gridConfig.cols, offsetX: gridConfig.offsetX, offsetY: gridConfig.offsetY },
                    metadata: {
                      ...(prev.metadata || {}),
                      cartiglio,
                    },
                  } : prev);

                  // Riconcilia gli ID ricariando i punti da Dexie
                  const saved = await getFloorPlanPoints(editorFloorPlan.id);
                  const reconciledPoints = saved.map(p => ({
                    id: p.id,
                    type: p.pointType as import('./components/FloorPlanCanvas').CanvasPoint['type'],
                    pointX: p.pointX,
                    pointY: p.pointY,
                    labelX: p.labelX,
                    labelY: p.labelY,
                    labelText: p.metadata?.labelText || ['Punto'],
                    perimeterPoints: p.perimeterPoints,
                    mappingEntryId: p.mappingEntryId,
                    structureEntryId: p.structureEntryId,
                    labelBackgroundColor: p.metadata?.labelBackgroundColor,
                    labelTextColor: p.metadata?.labelTextColor,
                    eiRating: p.eiRating,
                  }));
                  setEditorInitialPoints(reconciledPoints);

                  alert('Planimetria salvata!');
                } catch (err) {
                  console.error('Error saving floor plan:', err);
                  alert('Errore nel salvataggio');
                }
              }}
              onClose={handleBackFromFloorPlanEditor}
              onReorderPoints={handleReorderEditorPoints}
              onNewPointRecord={handleNewPointRecord}
              linkedRecordUpdate={editorRecordUpdate}
              onDeleteLinkedRecord={handleDeleteLinkedRecord}
              opensMappingEntryInModal
              onOpenMappingEntry={async (entryId) => {
                // La scheda si apre in un modal sopra l'editor: chiudendola si torna
                // alla planimetria senza doverla riaprire. La voce di cronologia
                // conserva `view`, così consumandola col tasto indietro App non naviga.
                const mappingEntry = await getMappingEntry(entryId);
                if (mappingEntry) {
                  openEditorRecordModal({ kind: 'mapping', entry: mappingEntry, initialStep: 1 });
                  return;
                }
                const structureEntry = await getStructureEntry(entryId);
                if (structureEntry) {
                  openEditorRecordModal({ kind: 'structure', entry: structureEntry, initialStep: 1 });
                }
              }}
            />

            {/* Scheda mappatura/struttura in modal sopra l'editor: si modifica e si
                salva senza uscire dalla planimetria. I wizard, su una scheda
                esistente, chiamano onBack sia dopo il salvataggio sia annullando. */}
            {editorRecordModal && (
              <div className="record-modal-overlay">
                {editorRecordModal.kind === 'mapping' ? (
                  <MappingWizard
                    project={editorProject}
                    currentUser={currentUser}
                    onBack={handleCloseEditorRecordModal}
                    onSaved={handleCloseEditorRecordModal}
                    editingEntry={editorRecordModal.entry}
                    onSync={handleManualSync}
                    isSyncing={syncStats.isSyncing}
                    initialStep={editorRecordModal.initialStep}
                    onTypologiesChanged={handleTypologiesChanged}
                    openedFromFloorPlanEditor
                  />
                ) : (
                  <StructureWizard
                    project={editorProject}
                    currentUser={currentUser}
                    onBack={handleCloseEditorRecordModal}
                    onSaved={handleCloseEditorRecordModal}
                    editingEntry={editorRecordModal.entry}
                    onSync={handleManualSync}
                    isSyncing={syncStats.isSyncing}
                    initialStep={editorRecordModal.initialStep}
                    onTypologiesChanged={handleTypologiesChanged}
                    openedFromFloorPlanEditor
                  />
                )}
              </div>
            )}
            </>
          );
        }
        return null;

      case 'reports':
        return (
          <ReportsPage
            vistaProvenienza={vistaPrimaDelleSegnalazioni.current}
            onBack={() => setCurrentView('tabs')}
          />
        );

      case 'tabs':
      default: {
        const tabContent = (
          <div className="flex-1 flex flex-col overflow-hidden">
            {activeTab === 'dashboard' && (
              <Dashboard
                currentUser={currentUser}
                syncStats={syncStats}
                syncProgress={syncProgress}
                isOnline={isOnline}
                onNavigateToProject={handleViewProject}
                onAddMapping={handleEnterMapping}
                onCreateProject={handleCreateProject}
                onManualSync={handleManualSync}
              />
            )}
            {activeTab === 'projects' && (
              <ProjectList
                currentUser={currentUser}
                onEditProject={handleEditProject}
                onDeleteProject={handleDeleteProject}
                onViewProject={handleViewProject}
                onEnterMapping={handleEnterMapping}
              />
            )}
            {activeTab === 'maps' && (
              <MapsOverview
                currentUser={currentUser}
                onOpenFloorPlan={handleOpenFloorPlanEditor}
                onOpenStandaloneEditor={handleOpenStandaloneEditor}
                onNavigateToProject={handleViewProject}
              />
            )}
            {activeTab === 'settings' && (
              <SettingsPage
                currentUser={currentUser}
                syncStats={syncStats}
                isOnline={isOnline}
                onLogout={handleLogout}
                onManualSync={handleManualSync}
                onClearAndSync={handleClearAndSync}
                onOpenReports={handleOpenReports}
              />
            )}
          </div>
        );

        if (isDesktop) {
          return (
            <div className="flex flex-1 overflow-hidden">
              <DesktopSidebar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                pendingSyncCount={syncStats.pendingCount}
                isOnline={isOnline}
              />
              {tabContent}
            </div>
          );
        }

        return (
          <>
            {tabContent}
            <BottomTabBar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              pendingSyncCount={syncStats.pendingCount}
            />
          </>
        );
      }
    }
  };

  return (
    <div className={`App${isDesktop ? ' app-desktop' : ''}`}>
      {/* Offline indicator */}
      {!isOnline && currentView === 'tabs' && (
        <div className="bg-warning text-white text-xs font-medium text-center py-2 px-4">
          Sei offline. Le modifiche verranno sincronizzate al ritorno della connessione.
        </div>
      )}

      <ErrorBoundary>
        <Suspense fallback={
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-3 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        }>
          {renderContent()}
        </Suspense>
      </ErrorBoundary>

      <UpdateNotification registration={swRegistration} />
    </div>
  );
};

export default App;
